import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  FormHelperText,
  Alert,
  LinearProgress,
  Grid,
  OutlinedInput,
  SelectChangeEvent,
  Tabs,
  Tab,
  Divider,
  Avatar,
  CardActions,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Save as SaveIcon,
  Preview as PreviewIcon,
  Public as PublicIcon,
  Drafts as DraftsIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  JobType,
  CompanyProfileFormData,
  UpdateCompanyProfileRequest,
  ValidationErrors,
} from '../../types/company-profile';
import { EMPLOYEE_COUNT_RANGES } from '../../constants/company';
import { CompanyImageUploader } from './CompanyImageUploader';
import { CompanyLogoUploader } from './CompanyLogoUploader';

// バリデーションスキーマ
const validationSchema = yup.object({
  business_content: yup
    .string()
    .max(500, '事業内容・強みは500文字以内で入力してください'),
  recruiting_message: yup
    .string()
    .max(500, '採用メッセージは500文字以内で入力してください'),
  target_candidates: yup
    .string()
    .max(300, '求める人材像は300文字以内で入力してください'),
  job_types: yup
    .array()
    .of(yup.string())
    .max(5, '募集職種は5つまで選択できます'),
  company_images: yup
    .array()
    .of(yup.string())
    .max(3, '企業画像は3枚まで選択できます'),
  is_published: yup.boolean().required('掲載ステータスは必須です'),
  logo_url: yup
    .string()
    .url('ロゴURLは有効なURLを入力してください')
    .max(500, 'ロゴURLは500文字以内で入力してください')
    .nullable()
    .transform((value) => (value ? value : null)),
});

interface CompanyProfileFormProps {
  companyId: number;
  initialData?: Partial<CompanyProfileFormData>;
  onSubmit: (data: UpdateCompanyProfileRequest) => Promise<void>;
  isSaving?: boolean;
  validationErrors?: ValidationErrors;
}

export const CompanyProfileForm: React.FC<CompanyProfileFormProps> = ({
  companyId,
  initialData,
  onSubmit,
  isSaving = false,
  validationErrors = {},
}) => {
  const [selectedJobTypes, setSelectedJobTypes] = useState<JobType[]>(
    initialData?.job_types || []
  );
  const [selectedImages, setSelectedImages] = useState<string[]>(
    initialData?.company_images || []
  );
  const [logoInputMethod, setLogoInputMethod] = useState<'url' | 'upload'>(
    'url'
  );

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
    reset,
  } = useForm<CompanyProfileFormData>({
    resolver: yupResolver(validationSchema) as any,
    defaultValues: {
      business_content: initialData?.business_content || '',
      recruiting_message: initialData?.recruiting_message || '',
      job_types: initialData?.job_types || [],
      target_candidates: initialData?.target_candidates || '',
      company_images: initialData?.company_images || [],
      is_published: initialData?.is_published || false,
      logo_url: initialData?.logo_url || '',
      employee_count_range: initialData?.employee_count_range || '',
    },
  });

  // 初期データが変更された場合にフォームをリセット
  useEffect(() => {
    if (initialData) {
      reset({
        business_content: initialData.business_content || '',
        recruiting_message: initialData.recruiting_message || '',
        job_types: initialData.job_types || [],
        target_candidates: initialData.target_candidates || '',
        company_images: initialData.company_images || [],
        is_published: initialData.is_published || false,
        logo_url: initialData.logo_url || '',
        employee_count_range: initialData.employee_count_range || '',
      });
      setSelectedJobTypes(initialData.job_types || []);
      setSelectedImages(initialData.company_images || []);
    }
  }, [initialData, reset]);

  // フォームデータの監視
  const watchedData = watch();
  const businessContentLength = watchedData.business_content?.length || 0;
  const recruitingMessageLength = watchedData.recruiting_message?.length || 0;
  const targetCandidatesLength = watchedData.target_candidates?.length || 0;

  // 募集職種の変更処理
  const handleJobTypesChange = (event: SelectChangeEvent<JobType[]>) => {
    const value = event.target.value as JobType[];
    setSelectedJobTypes(value);
    setValue('job_types', value, { shouldDirty: true });
  };

  // 企業画像の変更処理
  const handleImagesChange = (imageUrls: string[]) => {
    setSelectedImages(imageUrls);
    setValue('company_images', imageUrls, { shouldDirty: true });
  };

  // ロゴの変更処理
  const handleLogoChange = (logoUrl: string | null) => {
    setValue('logo_url', logoUrl || '', { shouldDirty: true });
  };

  // フォーム送信処理（要件定義準拠：シンプルな保存）
  const onFormSubmit = async (data: CompanyProfileFormData) => {
    await onSubmit({
      business_content: data.business_content || undefined,
      recruiting_message: data.recruiting_message || undefined,
      job_types:
        data.job_types && data.job_types.length > 0
          ? data.job_types
          : undefined,
      target_candidates: data.target_candidates || undefined,
      company_images:
        selectedImages && selectedImages.length > 0
          ? selectedImages
          : undefined,
      is_published: data.is_published,
      logo_url: data.logo_url?.trim() || undefined,
      employee_count_range: data.employee_count_range || undefined,
    });
  };

  return (
    <Card sx={{ maxWidth: 900, mx: 'auto' }}>
      <CardContent sx={{ p: 4 }}>
        <Typography
          variant="h5"
          component="h2"
          gutterBottom
          sx={{ fontWeight: 600, mb: 3 }}
        >
          企業プロフィール編集
        </Typography>

        {Object.keys(validationErrors).length > 0 && (
          <Alert severity="error" sx={{ mb: 3 }}>
            入力内容に問題があります。エラーメッセージを確認してください。
          </Alert>
        )}

        {isSaving && <LinearProgress sx={{ mb: 3 }} />}

        <form>
          <Grid container spacing={3}>
            {/* 事業内容・強み */}
            <Grid item xs={12}>
              <Controller
                name="business_content"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="事業内容・強み"
                    multiline
                    rows={4}
                    fullWidth
                    placeholder="御社の事業内容や強みをアピールしてください"
                    error={
                      !!errors.business_content ||
                      !!validationErrors.business_content
                    }
                    helperText={
                      errors.business_content?.message ||
                      validationErrors.business_content ||
                      `${businessContentLength}/500文字`
                    }
                    disabled={isSaving}
                  />
                )}
              />
            </Grid>

            {/* 採用メッセージ */}
            <Grid item xs={12}>
              <Controller
                name="recruiting_message"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="採用メッセージ"
                    multiline
                    rows={4}
                    fullWidth
                    placeholder="応募者に向けたメッセージをお書きください"
                    error={
                      !!errors.recruiting_message ||
                      !!validationErrors.recruiting_message
                    }
                    helperText={
                      errors.recruiting_message?.message ||
                      validationErrors.recruiting_message ||
                      `${recruitingMessageLength}/500文字`
                    }
                    disabled={isSaving}
                  />
                )}
              />
            </Grid>

            {/* 募集職種 */}
            <Grid item xs={12}>
              <FormControl
                fullWidth
                error={!!errors.job_types || !!validationErrors.job_types}
                disabled={isSaving}
              >
                <InputLabel>募集職種</InputLabel>
                <Select
                  multiple
                  value={selectedJobTypes}
                  onChange={handleJobTypesChange}
                  input={<OutlinedInput label="募集職種" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {Object.values(JobType).map((jobType) => (
                    <MenuItem key={jobType} value={jobType}>
                      {jobType}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {errors.job_types?.message ||
                    validationErrors.job_types ||
                    '最大5つまで選択できます'}
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* 求める人材像 */}
            <Grid item xs={12}>
              <Controller
                name="target_candidates"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="求める人材像"
                    multiline
                    rows={3}
                    fullWidth
                    placeholder="どのような人材を求めているかお書きください"
                    error={
                      !!errors.target_candidates ||
                      !!validationErrors.target_candidates
                    }
                    helperText={
                      errors.target_candidates?.message ||
                      validationErrors.target_candidates ||
                      `${targetCandidatesLength}/300文字`
                    }
                    disabled={isSaving}
                  />
                )}
              />
            </Grid>

            {/* 企業プロフィール画像 */}
            <Grid item xs={12}>
              <CompanyImageUploader
                companyId={companyId}
                value={selectedImages}
                onChange={handleImagesChange}
                disabled={isSaving}
                error={
                  errors.company_images?.message ||
                  validationErrors.company_images
                }
              />
            </Grid>

            {/* 企業ロゴ設定 */}
            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                企業ロゴ設定
              </Typography>

              <Tabs
                value={logoInputMethod}
                onChange={(_, newValue) => setLogoInputMethod(newValue)}
                sx={{ mb: 2 }}
              >
                <Tab label="URLで指定" value="url" />
                <Tab label="ファイルをアップロード" value="upload" />
              </Tabs>

              {logoInputMethod === 'url' ? (
                <Box>
                  {/* 現在のロゴ表示（URL指定） */}
                  {watchedData.logo_url && (
                    <Box sx={{ mb: 2 }}>
                      <Card sx={{ maxWidth: 300 }}>
                        <CardContent sx={{ textAlign: 'center', pb: 1 }}>
                          <Avatar
                            src={watchedData.logo_url}
                            alt="企業ロゴ"
                            sx={{
                              width: 120,
                              height: 120,
                              mx: 'auto',
                              mb: 1,
                              bgcolor: 'common.white',
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <BusinessIcon
                              sx={{ fontSize: 40, color: 'grey.500' }}
                            />
                          </Avatar>
                          <Typography variant="body2" color="text.secondary">
                            現在のロゴ
                          </Typography>
                        </CardContent>
                        <CardActions sx={{ justifyContent: 'center', pt: 0 }}>
                          <Tooltip title="ロゴを削除">
                            <IconButton
                              size="small"
                              onClick={() => handleLogoChange(null)}
                              disabled={isSaving}
                              color="error"
                            >
                              <CloseIcon />
                            </IconButton>
                          </Tooltip>
                        </CardActions>
                      </Card>
                    </Box>
                  )}

                  <Controller
                    name="logo_url"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="企業ロゴURL"
                        fullWidth
                        placeholder="https://example.com/logo.png"
                        error={!!errors.logo_url || !!validationErrors.logo_url}
                        helperText={
                          errors.logo_url?.message ||
                          validationErrors.logo_url ||
                          '学生向けプロフィールやスポンサー表示で利用される企業ロゴのURLを入力してください'
                        }
                        disabled={isSaving}
                      />
                    )}
                  />
                </Box>
              ) : (
                <Controller
                  name="logo_url"
                  control={control}
                  render={({ field }) => (
                    <CompanyLogoUploader
                      companyId={companyId}
                      value={field.value}
                      onChange={handleLogoChange}
                      disabled={isSaving}
                      error={
                        errors.logo_url?.message || validationErrors.logo_url
                      }
                    />
                  )}
                />
              )}
            </Grid>

            {/* 従業員数規模 */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="employee_count_range"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth disabled={isSaving}>
                    <InputLabel>従業員数規模</InputLabel>
                    <Select
                      {...field}
                      label="従業員数規模"
                      value={field.value || ''}
                    >
                      <MenuItem value="">未設定</MenuItem>
                      {EMPLOYEE_COUNT_RANGES.map((item) => (
                        <MenuItem key={item.value} value={item.value}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {validationErrors.employee_count_range}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>

            {/* 掲載ステータス（要件定義準拠：シンプルな公開・非公開制御） */}
            <Grid item xs={12}>
              <Controller
                name="is_published"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth disabled={isSaving}>
                    <InputLabel>掲載ステータス</InputLabel>
                    <Select
                      {...field}
                      label="掲載ステータス"
                      value={field.value ? 'published' : 'draft'}
                      onChange={(e) =>
                        field.onChange(e.target.value === 'published')
                      }
                    >
                      <MenuItem value="draft">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <DraftsIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          非公開
                        </Box>
                      </MenuItem>
                      <MenuItem value="published">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PublicIcon sx={{ mr: 1, color: 'success.main' }} />
                          公開
                        </Box>
                      </MenuItem>
                    </Select>
                    <FormHelperText>
                      {errors.is_published?.message ||
                        validationErrors.is_published}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>

          {/* アクションボタン（要件定義準拠：シンプルな保存） */}
          <Box
            sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}
          >
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSubmit(onFormSubmit)}
              disabled={isSaving || !isDirty}
              color="primary"
            >
              {isSaving ? '保存中...' : '保存'}
            </Button>
          </Box>
        </form>
      </CardContent>
    </Card>
  );
};
